import { normalizeWithMap } from './normalize';
import type { Chapter45Anchor, Chapter45Block } from './types';

// ============================================================================
// Public Types
// ============================================================================

/** Input segment from LLM response */
export interface SegmentExactInput {
  /** Block ID (e.g., "p-10", "t-5-r1-c0") */
  id: string;
  /** Exact text from the block (must be verbatim substring) */
  tekstExact: string;
}

/** Input quote group from LLM response */
export interface QuoteGroupInput {
  /** Index for grouping (segments with same quoteIndex are displayed together) */
  quoteIndex: number;
  /** Array of exact text segments for this quote group */
  segmentyExact: SegmentExactInput[];
}

/** Result of anchoring segments with block IDs */
export interface SegmentAnchoringResult {
  /** Successfully anchored segments with positions */
  anchors: Chapter45Anchor[];
  /** Segments that couldn't be matched */
  unmatchedSegments: UnmatchedSegment[];
  /** Derived display texts for UI (each quote group joined with "(...)" ) */
  displayTexts: string[];
}

// ============================================================================
// Internal Types
// ============================================================================

interface UnmatchedSegment {
  id: string;
  tekstExact: string;
  reason: string;
}

/** Preprocessed block data for matching */
interface BlockInfo {
  text: string;
  norm: string;
  map: number[];
  blockIndex: number;
}

/** Info about a matched/unmatched segment for display text generation */
interface SegmentInfo {
  text: string;
  blockId: string | null;
  start: number | null;
  end: number | null;
  blockText: string | null;
}

/** Tracking data for a quote group during processing */
interface QuoteGroupData {
  displayText: string;
  segmentInfos: SegmentInfo[];
  anchors: Chapter45Anchor[];
  blockIds: Set<string>;
  /** Position of first anchor for sorting (blockIndex * 1e6 + start) */
  firstPosition: number;
}

/** Result of matching a single quote group's segments against blocks */
interface GroupMatchResult {
  segmentInfos: SegmentInfo[];
  anchors: Chapter45Anchor[];
  blockIds: Set<string>;
  firstPosition: number;
  unmatched: UnmatchedSegment[];
}

// ============================================================================
// Document Position
// ============================================================================

/** Compute a sortable document position from block index + character offset */
function docPosition(blockIndex: number, charOffset: number): number {
  return blockIndex * 1e6 + charOffset;
}

// ============================================================================
// Block Map Building
// ============================================================================

interface BlockMaps {
  /** Full block info for matching (text, normalized text, position map) */
  blockMap: Map<string, BlockInfo>;
  /** Lightweight blockId → blockIndex for sorting */
  blockIndexMap: Map<string, number>;
  /** blockId → raw text for overlap merge checking */
  blockTexts: Map<string, string>;
}

function buildBlockMaps(chapterBlocks: Chapter45Block[]): BlockMaps {
  const blockMap = new Map<string, BlockInfo>();

  chapterBlocks.forEach((block, blockIndex) => {
    const add = (id: string, text: string) => {
      const { norm, map } = normalizeWithMap(text);
      blockMap.set(id, { text, norm, map, blockIndex });
    };

    switch (block.type) {
      case 'heading':
      case 'paragraph':
        add(block.blockId, block.text);
        break;
      case 'list':
        add(block.blockId, block.items.map((i) => i.text).join('\n'));
        block.items.forEach((item) => add(item.blockId, item.text));
        break;
      case 'table':
        block.rows.forEach((row, r) =>
          row.forEach((cell, c) =>
            add(block.cellBlockIds[r]![c]!, cell.content),
          ),
        );
        break;
      case 'html-block':
        block.rows.forEach((row, r) =>
          row.forEach((cell, c) =>
            add(block.cellBlockIds[r]![c]!, cell.content),
          ),
        );
        block.legendParagraphs.forEach((para) => add(para.blockId, para.text));
        break;
      case 'hr':
        break;
    }
  });

  const blockIndexMap = new Map<string, number>();
  const blockTexts = new Map<string, string>();
  for (const [id, info] of blockMap.entries()) {
    blockIndexMap.set(id, info.blockIndex);
    blockTexts.set(id, info.text);
  }

  return { blockMap, blockIndexMap, blockTexts };
}

// ============================================================================
// Text Matching
// ============================================================================

const MARKDOWN_MARKER_CHARS = ['*', '_'];

/** Expand anchor boundaries to include surrounding markdown markers (*bold*, _italic_) */
function expandToMarkdownBoundaries(
  text: string,
  start: number,
  end: number,
): { start: number; end: number } {
  let s = start;
  let e = end;
  while (s > 0 && MARKDOWN_MARKER_CHARS.includes(text[s - 1]!)) s--;
  while (e < text.length && MARKDOWN_MARKER_CHARS.includes(text[e]!)) e++;
  return { start: s, end: e };
}

/** Try direct or normalized exact match within a block */
function tryExactMatch(
  segmentText: string,
  block: BlockInfo,
): { start: number; end: number } | null {
  // Direct exact match
  const directIdx = block.text.indexOf(segmentText);
  if (directIdx !== -1) {
    return expandToMarkdownBoundaries(
      block.text,
      directIdx,
      directIdx + segmentText.length,
    );
  }

  // Normalized match
  const segNorm = normalizeWithMap(segmentText).norm;
  if (!segNorm || !block.norm) return null;

  const normIdx = block.norm.indexOf(segNorm);
  if (normIdx === -1) return null;

  const startOrig = block.map[normIdx] ?? 0;
  const endOrig = (block.map[normIdx + segNorm.length - 1] ?? startOrig) + 1;
  return expandToMarkdownBoundaries(block.text, startOrig, endOrig);
}

/**
 * Levenshtein distance with early termination.
 * Returns maxDist + 1 if distance exceeds maxDist.
 */
function levenshteinDistance(a: string, b: string, maxDist: number): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  if (a.length > b.length) [a, b] = [b, a];

  const aLen = a.length;
  const bLen = b.length;
  if (bLen - aLen > maxDist) return maxDist + 1;

  let prevRow = Array.from({ length: aLen + 1 }, (_, i) => i);
  let currRow = new Array<number>(aLen + 1);

  for (let j = 1; j <= bLen; j++) {
    currRow[0] = j;
    let minInRow = j;
    for (let i = 1; i <= aLen; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      currRow[i] = Math.min(
        prevRow[i]! + 1,
        currRow[i - 1]! + 1,
        prevRow[i - 1]! + cost,
      );
      minInRow = Math.min(minInRow, currRow[i]!);
    }
    if (minInRow > maxDist) return maxDist + 1;
    [prevRow, currRow] = [currRow, prevRow];
  }

  return prevRow[aLen]!;
}

/** Try fuzzy (Levenshtein) match within a block. Requires ≥10 normalized chars. */
function tryFuzzyMatch(
  segmentText: string,
  block: BlockInfo,
): { start: number; end: number } | null {
  const segNorm = normalizeWithMap(segmentText).norm;
  if (!segNorm || segNorm.length < 10) return null;
  if (!block.norm || block.norm.length < segNorm.length * 0.7) return null;

  const maxDist = Math.ceil(segNorm.length * 0.1);
  const windowMin = Math.floor(segNorm.length * 0.85);
  const windowMax = Math.ceil(segNorm.length * 1.15);
  let best: { start: number; end: number; distance: number } | null = null;

  for (let s = 0; s <= block.norm.length - windowMin; s += 3) {
    for (const ws of [segNorm.length - 5, segNorm.length, segNorm.length + 5]) {
      if (ws < windowMin || ws > windowMax || s + ws > block.norm.length)
        continue;
      const dist = levenshteinDistance(
        segNorm,
        block.norm.slice(s, s + ws),
        maxDist,
      );
      if (dist <= maxDist && (!best || dist < best.distance)) {
        const startOrig = block.map[s] ?? 0;
        const endOrig = (block.map[s + ws - 1] ?? startOrig) + 1;
        best = { start: startOrig, end: endOrig, distance: dist };
      }
    }
  }

  return best
    ? expandToMarkdownBoundaries(block.text, best.start, best.end)
    : null;
}

// ============================================================================
// Segment Matching (per quote group)
// ============================================================================

/** Match all segments in a quote group against blocks. */
function matchQuoteGroup(
  group: QuoteGroupInput,
  blockMap: Map<string, BlockInfo>,
): GroupMatchResult {
  const segmentInfos: SegmentInfo[] = [];
  const anchors: Chapter45Anchor[] = [];
  const blockIds = new Set<string>();
  const unmatched: UnmatchedSegment[] = [];
  let firstPosition = Infinity;

  for (const seg of group.segmentyExact) {
    blockIds.add(seg.id);
    const block = blockMap.get(seg.id);

    if (!block) {
      unmatched.push({
        id: seg.id,
        tekstExact: seg.tekstExact,
        reason: `Block ID not found: ${seg.id}`,
      });
      segmentInfos.push({
        text: seg.tekstExact,
        blockId: null,
        start: null,
        end: null,
        blockText: null,
      });
      continue;
    }

    const match =
      tryExactMatch(seg.tekstExact, block) ??
      tryFuzzyMatch(seg.tekstExact, block);

    if (match) {
      anchors.push({
        blockId: seg.id,
        start: match.start,
        end: match.end,
        quoteIndex: group.quoteIndex,
      });
      const pos = docPosition(block.blockIndex, match.start);
      if (pos < firstPosition) firstPosition = pos;
      segmentInfos.push({
        text: block.text.slice(match.start, match.end),
        blockId: seg.id,
        start: match.start,
        end: match.end,
        blockText: block.text,
      });
    } else {
      const normLen = normalizeWithMap(seg.tekstExact).norm.length;
      unmatched.push({
        id: seg.id,
        tekstExact: seg.tekstExact,
        reason:
          normLen < 10
            ? `No exact match in block ${seg.id} (${normLen} chars - too short for fuzzy matching, need ≥10)`
            : `No match found in block ${seg.id} (exact and fuzzy both failed)`,
      });
      segmentInfos.push({
        text: seg.tekstExact,
        blockId: seg.id,
        start: null,
        end: null,
        blockText: block.text,
      });
    }
  }

  return { segmentInfos, anchors, blockIds, firstPosition, unmatched };
}

// ============================================================================
// Segment Sorting & Joining
// ============================================================================

/** Sort segments by document position (blockIndex, start). Unpositioned → end. */
function sortSegments(
  segments: SegmentInfo[],
  blockIndexMap: Map<string, number>,
): SegmentInfo[] {
  return [...segments].sort((a, b) => {
    const aPos = segmentPosition(a, blockIndexMap);
    const bPos = segmentPosition(b, blockIndexMap);
    return aPos - bPos;
  });
}

function segmentPosition(
  seg: SegmentInfo,
  blockIndexMap: Map<string, number>,
): number {
  if (seg.blockId !== null && seg.start !== null) {
    const idx = blockIndexMap.get(seg.blockId);
    if (idx !== undefined) return docPosition(idx, seg.start);
  }
  return Infinity;
}

/**
 * Join segments intelligently.
 * Adjacent segments (same block, whitespace gap ≤5 chars) → join directly.
 * Non-adjacent → join with (...).
 */
function joinSegments(segments: SegmentInfo[]): string {
  if (segments.length === 0) return '';
  if (segments.length === 1) return segments[0]!.text;

  const parts: string[] = [segments[0]!.text];

  for (let i = 1; i < segments.length; i++) {
    const prev = segments[i - 1]!;
    const curr = segments[i]!;

    if (areAdjacent(prev, curr)) {
      const gap = prev.blockText!.slice(prev.end!, curr.start!);
      parts.push(gap.trim() === '' ? ' ' : gap);
    } else {
      parts.push(' (...) ');
    }
    parts.push(curr.text);
  }

  return parts.join('');
}

/** Two segments are adjacent if same block, gap ≤5 chars of whitespace/punctuation */
function areAdjacent(prev: SegmentInfo, curr: SegmentInfo): boolean {
  if (
    prev.blockId === null ||
    curr.blockId === null ||
    prev.end === null ||
    curr.start === null ||
    prev.blockText === null ||
    prev.blockId !== curr.blockId ||
    curr.start < prev.end
  ) {
    return false;
  }
  const gap = prev.blockText.slice(prev.end, curr.start);
  return /^[\s.,;:]*$/.test(gap) && gap.length <= 5;
}

// ============================================================================
// Group Merging (consecutive groups sharing block IDs)
// ============================================================================

/**
 * Merge consecutive quote groups that reference the same block.
 * This handles LLMs splitting same-paragraph content into separate quotes.
 */
function mergeAdjacentGroups(
  sorted: QuoteGroupData[],
  blockIndexMap: Map<string, number>,
): QuoteGroupData[] {
  const merged: QuoteGroupData[] = [];

  for (const group of sorted) {
    const last = merged[merged.length - 1];

    const sharesBlock =
      last && [...group.blockIds].some((id) => last.blockIds.has(id));

    if (sharesBlock && last) {
      last.segmentInfos.push(...group.segmentInfos);
      last.segmentInfos = sortSegments(last.segmentInfos, blockIndexMap);
      last.displayText = joinSegments(last.segmentInfos);
      last.anchors.push(...group.anchors);
      for (const id of group.blockIds) last.blockIds.add(id);
    } else {
      merged.push({
        ...group,
        segmentInfos: [...group.segmentInfos],
        anchors: [...group.anchors],
        blockIds: new Set(group.blockIds),
      });
    }
  }

  return merged;
}

// ============================================================================
// Anchor Overlap Merging
// ============================================================================

/** Merge overlapping or whitespace-adjacent anchors within the same block */
function mergeOverlappingAnchors(
  anchors: Chapter45Anchor[],
  blockTexts: Map<string, string>,
): Chapter45Anchor[] {
  if (anchors.length === 0) return [];

  // Group by block, sort within each block by start, merge overlapping
  const byBlock = new Map<
    string,
    Array<{ anchor: Chapter45Anchor; idx: number }>
  >();
  anchors.forEach((a, i) => {
    const list = byBlock.get(a.blockId) ?? [];
    list.push({ anchor: a, idx: i });
    byBlock.set(a.blockId, list);
  });

  const merged: Array<{ anchor: Chapter45Anchor; idx: number }> = [];

  for (const [blockId, list] of byBlock.entries()) {
    list.sort((a, b) => a.anchor.start - b.anchor.start);
    const text = blockTexts.get(blockId) ?? '';
    const blockMerged: typeof list = [];

    for (const cur of list) {
      const last = blockMerged[blockMerged.length - 1];
      const gap = last ? text.slice(last.anchor.end, cur.anchor.start) : '';
      const shouldMerge =
        last &&
        (cur.anchor.start < last.anchor.end ||
          (/^[\s.,;:]*$/.test(gap) && gap.length <= 5));

      if (shouldMerge && last) {
        last.anchor.end = Math.max(last.anchor.end, cur.anchor.end);
      } else {
        blockMerged.push({
          anchor: {
            blockId,
            start: cur.anchor.start,
            end: cur.anchor.end,
            quoteIndex: cur.anchor.quoteIndex,
          },
          idx: cur.idx,
        });
      }
    }
    merged.push(...blockMerged);
  }

  merged.sort((a, b) => a.idx - b.idx);
  return merged.map((x) => x.anchor);
}

// ============================================================================
// Final Quote Ordering
// ============================================================================

/**
 * Defensive final sort: reorder quotes so that quote 0 = topmost highlight,
 * quote 1 = next, etc. Reorders displayTexts and remaps quoteIndex in anchors.
 */
function ensureQuoteDocumentOrder(
  displayTexts: string[],
  anchors: Chapter45Anchor[],
  blockIndexMap: Map<string, number>,
): { displayTexts: string[]; anchors: Chapter45Anchor[] } {
  if (displayTexts.length <= 1) return { displayTexts, anchors };

  // Find earliest anchor position per quoteIndex
  const minPos = new Map<number, number>();
  for (const a of anchors) {
    const qi = a.quoteIndex ?? 0;
    const idx = blockIndexMap.get(a.blockId);
    const pos = idx !== undefined ? docPosition(idx, a.start) : Infinity;
    const cur = minPos.get(qi) ?? Infinity;
    if (pos < cur) minPos.set(qi, pos);
  }

  // Build sort order
  const order = displayTexts
    .map((_, i) => ({ old: i, pos: minPos.get(i) ?? Infinity }))
    .sort((a, b) => a.pos - b.pos);

  // Skip if already sorted (common case)
  if (order.every((item, i) => item.old === i)) {
    return { displayTexts, anchors };
  }

  // Remap
  const oldToNew = new Map<number, number>();
  order.forEach((item, newIdx) => oldToNew.set(item.old, newIdx));

  return {
    displayTexts: order.map((item) => displayTexts[item.old]!),
    anchors: anchors.map((a) => ({
      ...a,
      quoteIndex: oldToNew.get(a.quoteIndex ?? 0) ?? a.quoteIndex,
    })),
  };
}

// ============================================================================
// Main Anchoring Function
// ============================================================================

/**
 * Anchor LLM quote segments to chapter 4.5 blocks.
 *
 * Pipeline:
 * 1. Build block lookup maps
 * 2. Match each quote group's segments (exact → fuzzy)
 * 3. Sort segments within groups by document position
 * 4. Sort groups by document position, merge adjacent groups sharing blocks
 * 5. Merge overlapping anchors within same block
 * 6. Final defensive sort ensuring quote numbering matches document order
 */
export function anchorSegmentsToChapter45(
  chapterBlocks: Chapter45Block[],
  quoteGroups: QuoteGroupInput[],
): SegmentAnchoringResult {
  const { blockMap, blockIndexMap, blockTexts } = buildBlockMaps(chapterBlocks);

  const allUnmatched: UnmatchedSegment[] = [];
  const groups: QuoteGroupData[] = [];

  // Step 2-3: Match segments & sort within each group
  for (const group of quoteGroups) {
    const result = matchQuoteGroup(group, blockMap);
    allUnmatched.push(...result.unmatched);

    const sorted = sortSegments(result.segmentInfos, blockIndexMap);
    const displayText = joinSegments(sorted);

    if (displayText.length > 0) {
      groups.push({
        displayText,
        segmentInfos: sorted,
        anchors: result.anchors,
        blockIds: result.blockIds,
        // Unanchored groups sort to the end (not position 0)
        firstPosition:
          result.firstPosition === Infinity
            ? (group.quoteIndex + 1) * 1e9
            : result.firstPosition,
      });
    }
  }

  // Step 4: Sort groups by document position, then merge adjacent
  groups.sort((a, b) => a.firstPosition - b.firstPosition);
  const merged = mergeAdjacentGroups(groups, blockIndexMap);

  // Step 5: Build final arrays with renumbered quoteIndex, merge overlapping anchors
  const displayTexts: string[] = [];
  const numberedAnchors: Chapter45Anchor[] = [];

  merged.forEach((group, idx) => {
    displayTexts.push(group.displayText);
    for (const a of group.anchors) {
      numberedAnchors.push({ ...a, quoteIndex: idx });
    }
  });

  const dedupedAnchors = mergeOverlappingAnchors(numberedAnchors, blockTexts);

  // Step 6: Final defensive sort
  const final = ensureQuoteDocumentOrder(
    displayTexts,
    dedupedAnchors,
    blockIndexMap,
  );

  return {
    anchors: final.anchors,
    unmatchedSegments: allUnmatched,
    displayTexts: final.displayTexts,
  };
}

// ============================================================================
// Quote Overlap Detection
// ============================================================================

export interface QuoteOverlap {
  /** Quote index whose segments span across another quote */
  quoteIndex: number;
  /** The quote index that falls within the span */
  overlappedQuoteIndex: number;
  /** Block IDs of the spanning quote */
  spanningBlockIds: string[];
  /** Block IDs of the overlapped quote */
  overlappedBlockIds: string[];
}

/**
 * Detect overlapping quotes from raw LLM output.
 * A quote "overlaps" another if its segments span a block range that contains
 * blocks from a different quote.
 *
 * @param quoteGroups - Raw quote groups from LLM
 * @param chapterBlocks - Parsed ChPL blocks (used to build block index map)
 * @returns Array of detected overlaps (empty = no issues)
 */
export function detectQuoteOverlaps(
  quoteGroups: QuoteGroupInput[],
  chapterBlocks: Chapter45Block[],
): QuoteOverlap[] {
  const { blockIndexMap } = buildBlockMaps(chapterBlocks);
  // For each quote, compute the block index range it spans
  const quoteRanges: Array<{
    quoteIndex: number;
    blockIds: string[];
    minBlock: number;
    maxBlock: number;
  }> = [];

  for (const group of quoteGroups) {
    const blockIndices: number[] = [];
    const blockIds: string[] = [];

    for (const seg of group.segmentyExact) {
      const idx = blockIndexMap.get(seg.id);
      if (idx !== undefined) {
        blockIndices.push(idx);
        blockIds.push(seg.id);
      }
    }

    if (blockIndices.length > 0) {
      quoteRanges.push({
        quoteIndex: group.quoteIndex,
        blockIds,
        minBlock: Math.min(...blockIndices),
        maxBlock: Math.max(...blockIndices),
      });
    }
  }

  // Check each pair: does quote A's range contain any of quote B's blocks?
  const overlaps: QuoteOverlap[] = [];

  for (const a of quoteRanges) {
    if (a.minBlock === a.maxBlock) continue; // Single-block quotes can't span across anything

    for (const b of quoteRanges) {
      if (a.quoteIndex === b.quoteIndex) continue;

      // Check if any of B's blocks fall strictly inside A's span
      const bInside = b.blockIds.some((id) => {
        const idx = blockIndexMap.get(id);
        return idx !== undefined && idx > a.minBlock && idx < a.maxBlock;
      });

      if (bInside) {
        overlaps.push({
          quoteIndex: a.quoteIndex,
          overlappedQuoteIndex: b.quoteIndex,
          spanningBlockIds: a.blockIds,
          overlappedBlockIds: b.blockIds,
        });
      }
    }
  }

  return overlaps;
}
